import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import ParticleCanvas from "./ParticleCanvas";

export default function KineticHero() {
  const { t } = useLang();
  const h = t.home.hero;
  const lines = [
    { text: h.line1, cls: "text-slate-50" },
    { text: h.line2, cls: "text-stroke" },
    { text: h.line3, cls: "text-gradient-cyan" },
  ];

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="home-hero">
      <div className="absolute inset-0 hud-lines opacity-60" aria-hidden="true" />
      <ParticleCanvas className="absolute inset-0 w-full h-full opacity-70" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse 80% 60% at 30% 40%, rgba(0,240,255,0.07), transparent 60%), radial-gradient(ellipse 60% 50% at 85% 75%, rgba(15,98,254,0.08), transparent 55%)" }}
        aria-hidden="true"
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-24 w-full grid lg:grid-cols-[1.35fr_1fr] gap-14 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="inline-flex items-center gap-3 border border-sovereign/25 bg-sovereign/5 px-4 py-2"
            data-testid="hero-badge"
          >
            <span className="w-2 h-2 rounded-full bg-sovereign blink" aria-hidden="true" />
            <span className="font-mono text-[9px] sm:text-xs tracking-[0.2em] sm:tracking-[0.28em] uppercase text-sovereign font-bold">
              {h.badge}
            </span>
          </motion.div>

          <h1 className="mt-8 font-display font-extrabold uppercase leading-[1.02] tracking-tight text-3xl sm:text-6xl lg:text-7xl xl:text-[5.2rem]" data-testid="hero-title">
            {lines.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${l.cls}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.25 + i * 0.18, ease: [0.19, 1, 0.22, 1] }}
                >
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
            data-testid="hero-subtitle"
          >
            {h.sub}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95 }}
            className="mt-4 max-w-xl font-mono text-[11px] sm:text-xs tracking-[0.18em] uppercase text-slate-400 leading-loose"
          >
            {h.domains}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-4"
            data-testid="hero-ctas"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-3 bg-sovereign text-obsidian font-semibold px-7 py-3.5 text-sm tracking-wide uppercase transition-all duration-300 hover:shadow-[0_0_40px_rgba(0,240,255,0.35)] hover:-translate-y-0.5"
            >
              {h.ctaPrimary}
              <span className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true">→</span>
            </Link>
            <Link
              to="/partners"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-3 border border-slate-600 text-slate-200 font-medium px-7 py-3.5 text-sm tracking-wide uppercase transition-all duration-300 hover:border-sovereign/60 hover:text-sovereign"
            >
              {h.ctaSecondary}
            </Link>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.25 }}
            className="mt-10 text-sm text-slate-500"
          >
            {h.powered} <span className="text-slate-300 font-semibold">{h.delivered}</span>
          </motion.p>
        </div>

        <motion.aside
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, delay: 0.7 }}
          className="glass p-6 sm:p-8 relative overflow-hidden hidden lg:block"
          data-testid="hero-status-panel"
        >
          <div className="scanlines absolute inset-0 pointer-events-none" aria-hidden="true" />
          <div className="flex items-center justify-between">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-sovereign font-bold">{h.panelTitle}</p>
            <span className="w-2 h-2 rounded-full bg-emerald-400 blink" aria-hidden="true" />
          </div>
          <div className="mt-6 space-y-3">
            {t.home.certs.map((c, i) => (
              <motion.div
                key={c}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.9 + i * 0.12 }}
                className="flex items-center gap-3 border border-sovereign/10 bg-obsidian/60 px-4 py-3"
                data-testid={`hero-cert-chip-${i}`}
              >
                <span className="w-1.5 h-1.5 bg-sovereign" aria-hidden="true" />
                <span className="font-mono text-xs tracking-[0.2em] text-slate-200">{c}</span>
              </motion.div>
            ))}
          </div>
          <p className="mt-6 pt-5 border-t border-sovereign/10 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-400">
            {h.panelCerts}
          </p>
          <p className="mt-3 font-mono text-[10px] tracking-[0.22em] uppercase text-emerald-400">
            ● {h.panelStatus}
          </p>
        </motion.aside>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-slate-500"
        aria-hidden="true"
      >
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase">{h.scroll}</span>
        <div className="w-px h-10 bg-gradient-to-b from-sovereign/60 to-transparent" />
      </motion.div>
    </section>
  );
}
